import React, { useMemo, useState } from 'react';
import { css } from '@emotion/core';
import { SearchField, Text, useForm } from '@weave/design-system';
import { theme } from '@weave/theme-original';
import { Page } from 'components/shared';
import { useGetRoles, Role } from './api';

export const RolesPermissionsPage = () => {
  return (
    <Page title="Roles & Permissions">
      <RolesPermissionsContent />
    </Page>
  );
};

const RolesPermissionsContent = () => {
  const { data: roles, isLoading, isError, error } = useGetRoles();
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null);

  const { getFieldProps, values } = useForm({
    fields: {
      search: { value: '', type: 'text' },
    },
  });

  const filteredRoles = useMemo(() => {
    if (!roles) return [];
    const search = (values.search || '').toLowerCase();
    const filtered = !search
      ? [...roles]
      : roles.filter(
          (role) =>
            role.name.toLowerCase().includes(search) ||
            role.description.toLowerCase().includes(search) ||
            role.acls.some(
              (acl) =>
                acl.name.toLowerCase().includes(search) ||
                String(acl.value).includes(search)
            )
        );
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [roles, values.search]);

  const toggleExpand = (roleId: string) => {
    setExpandedRoleId((prev) => (prev === roleId ? null : roleId));
  };

  if (isLoading) return <Text>Loading roles...</Text>;
  if (isError) return <Text color="error">Error loading roles: {String(error)}</Text>;

  return (
    <div
      style={{
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        maxWidth: 900,
        margin: '0 auto',
      }}
    >
      <div style={{ marginBottom: theme.spacing(2), maxWidth: 480 }}>
        <SearchField {...getFieldProps('search')} aria-label="Search roles" />
      </div>
      <Text
        size="small"
        color="light"
        style={{ marginBottom: theme.spacing(1), display: 'block' }}
      >
        Showing {filteredRoles.length} of {roles?.length ?? 0} roles
      </Text>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(1)};
        `}
      >
        {filteredRoles.map((role) => (
          <RoleCard
            key={role.roleId}
            role={role}
            isExpanded={expandedRoleId === role.roleId}
            onToggle={() => toggleExpand(role.roleId)}
          />
        ))}
      </div>
    </div>
  );
};

type RoleCardProps = { role: Role; isExpanded: boolean; onToggle: () => void };

const RoleCard = ({ role, isExpanded, onToggle }: RoleCardProps) => (
  <div
    css={css`
      border: 1px solid ${isExpanded ? theme.colors.gray500 : theme.colors.gray300};
      border-radius: ${theme.borderRadius.medium};
      overflow: hidden;
      cursor: pointer;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      &:hover {
        border-color: ${theme.colors.gray500};
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      }
    `}
    onClick={onToggle}
  >
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
        background: ${isExpanded ? theme.colors.gray100 : '#fff'};
      `}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: theme.spacing(1),
            flexWrap: 'wrap',
          }}
        >
          <Text weight="bold" style={{ fontSize: 14 }}>
            {role.name}
          </Text>
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              padding: '1px 7px',
              borderRadius: 10,
              background: role.type === 'weave' ? '#e8f4fd' : theme.colors.gray200,
              color: role.type === 'weave' ? '#1a6fa8' : theme.colors.gray600,
              whiteSpace: 'nowrap',
            }}
          >
            {role.type}
          </span>
        </div>
        {role.description && role.description !== 'Needs Description' && (
          <Text size="small" color="light" style={{ lineHeight: 1.3, marginTop: 2 }}>
            {role.description}
          </Text>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(0.75),
          marginLeft: theme.spacing(2),
          flexShrink: 0,
        }}
      >
        {role.acls.length > 0 && (
          <span
            style={{ fontSize: 11, color: theme.colors.gray600, whiteSpace: 'nowrap' }}
          >
            {role.acls.length} ACL{role.acls.length !== 1 ? 's' : ''}
          </span>
        )}
        <span
          style={{
            fontSize: 9,
            color: theme.colors.gray500,
            transition: 'transform 0.15s ease',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          ▼
        </span>
      </div>
    </div>
    {isExpanded && (
      <div
        css={css`
          padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
          border-top: 1px solid ${theme.colors.gray300};
          background: ${theme.colors.gray100};
        `}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          css={css`
            display: grid;
            grid-template-columns: 70px 1fr;
            gap: ${theme.spacing(0.5)} ${theme.spacing(1.5)};
            margin-bottom: ${role.acls.length > 0 ? theme.spacing(1.5) : '0'};
            font-size: 12px;
          `}
        >
          <Text size="small" weight="bold" color="light">
            Role ID
          </Text>
          <Text size="small">{role.roleId}</Text>
          <Text size="small" weight="bold" color="light">
            UUID
          </Text>
          <Text
            size="small"
            style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}
          >
            {role.roleUuid}
          </Text>
          <Text size="small" weight="bold" color="light">
            Type
          </Text>
          <Text size="small">{role.type}</Text>
          {role.category && (
            <>
              <Text size="small" weight="bold" color="light">
                Category
              </Text>
              <Text size="small">{role.category}</Text>
            </>
          )}
          {role.header && (
            <>
              <Text size="small" weight="bold" color="light">
                Header
              </Text>
              <Text size="small">{role.header}</Text>
            </>
          )}
        </div>
        {role.acls.length > 0 ? (
          <div
            css={css`
              border: 1px solid ${theme.colors.gray300};
              border-radius: ${theme.borderRadius.small};
              overflow: hidden;
            `}
          >
            <table
              css={css`
                width: 100%;
                border-collapse: collapse;
                font-size: 12px;
                th,
                td {
                  text-align: left;
                  padding: ${theme.spacing(0.5)} ${theme.spacing(1.5)};
                }
                th {
                  background: ${theme.colors.gray200};
                  font-weight: 600;
                  font-size: 11px;
                  text-transform: uppercase;
                  letter-spacing: 0.3px;
                  color: ${theme.colors.gray600};
                }
                tbody tr:not(:last-child) td {
                  border-bottom: 1px solid ${theme.colors.gray200};
                }
              `}
            >
              <thead>
                <tr>
                  <th>ACL Name</th>
                  <th style={{ width: 70 }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {role.acls.map((acl) => (
                  <tr key={acl.name}>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{acl.name}</td>
                    <td>{acl.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Text size="small" color="light">
            No ACLs associated with this role.
          </Text>
        )}
      </div>
    )}
  </div>
);
